import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../services/api';
import { supabase, isSupabaseBrowserConfigured } from '../lib/supabaseClient';
import Notifications from './Notifications';
import ProfileMenu from './ProfileMenu';
import { shopsPath } from '../utils/workspacePaths';
import { extractAppScope, withCurrentScope } from '../utils/appRouteScope';
import { ZB_BACKEND_SESSION_CHANGED } from '../lib/appMode';
import './Header.css';

const SS_UID = 'zb_simple_uid';

function getProfileUserId(u) {
  if (u?.id) return String(u.id);
  if (typeof window === 'undefined') return '';
  try {
    return (
      localStorage.getItem(SS_UID) ||
      sessionStorage.getItem(SS_UID) ||
      ''
    );
  } catch {
    return '';
  }
}

function workspaceTitle(pathname) {
  const p = pathname || '';
  if (/\/app(?:\/|$)/.test(p)) return 'Dashboard';
  if (p.includes('/billing')) return 'Billing';
  if (p.includes('/inventory/product/')) return 'Product';
  if (p.includes('/inventory')) return 'Products';
  if (p.includes('/customers')) return 'Customers';
  if (p.includes('/suppliers')) return 'Suppliers';
  if (p.includes('/purchases')) return 'Purchases';
  if (p.includes('/expenses')) return 'Expenses';
  if (p.includes('/rate-list')) return 'Rate List';
  if (p.includes('/invoices')) return 'Invoices';
  if (p.includes('/sales')) return 'Sales';
  if (p.includes('/reports')) return 'Reports';
  if (p.includes('/categories')) return 'Categories';
  if (p.includes('/users')) return 'Users';
  if (p.includes('/support-tickets/') && p.includes('/chat')) return 'Ticket chat';
  if (p.includes('/support-tickets')) return 'Support Tickets';
  if (p.includes('/settings')) return 'Settings';
  return 'Workspace';
}

function isDashboardPath(pathname) {
  const scope = extractAppScope(pathname);
  if (!scope?.base) return false;
  const normalized = String(pathname || '').replace(/\/$/, '');
  return normalized === `${scope.base}/app` || /\/app$/.test(normalized);
}

const Header = ({ onMenuClick }) => {
  const { t } = useTranslation();
  const { user, activeShopId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [storeName, setStoreName] = useState('');

  const fetchStoreName = useCallback(async () => {
    let resolved = '';

    try {
      const response = await settingsAPI.get();
      const data = response.data;

      if (data && data.other_app_settings) {
        const otherSettings =
          typeof data.other_app_settings === 'string'
            ? JSON.parse(data.other_app_settings)
            : data.other_app_settings;

        const name =
          otherSettings?.shop_name ||
          otherSettings?.businessInfo?.shopName ||
          (typeof data.shop_name === 'string' ? data.shop_name : null);
        if (name && String(name).trim()) {
          resolved = String(name).trim();
        }
      } else if (data?.shop_name) {
        resolved = String(data.shop_name).trim();
      }
    } catch (error) {
      console.error('[Header] Error fetching store name:', error);
    }

    if (isSupabaseBrowserConfigured() && supabase && activeShopId) {
      try {
        const { data: shopRow, error } = await supabase
          .from('shops')
          .select('name')
          .eq('id', activeShopId)
          .maybeSingle();
        if (!error && shopRow?.name && String(shopRow.name).trim()) {
          resolved = String(shopRow.name).trim();
        }
      } catch {
        /* keep */
      }
    }

    setStoreName(resolved);
  }, [activeShopId]);

  useEffect(() => {
    if (user) fetchStoreName();
  }, [user, fetchStoreName]);

  useEffect(() => {
    const onShopDisplayUpdated = () => {
      if (user) fetchStoreName();
    };
    window.addEventListener('zb-shop-display-updated', onShopDisplayUpdated);
    return () => window.removeEventListener('zb-shop-display-updated', onShopDisplayUpdated);
  }, [user, fetchStoreName]);

  useEffect(() => {
    const onSess = () => {
      if (user) fetchStoreName();
    };
    window.addEventListener(ZB_BACKEND_SESSION_CHANGED, onSess);
    return () => window.removeEventListener(ZB_BACKEND_SESSION_CHANGED, onSess);
  }, [user, fetchStoreName]);

  if (!user) {
    return null;
  }

  const displayUserName = user?.full_name || user?.name || user?.username || 'User';
  const pageTitle = workspaceTitle(location.pathname);
  const shopsPickerTo = shopsPath(getProfileUserId(user));
  const inWorkspace = Boolean(extractAppScope(location.pathname)?.base);
  const onDashboard = inWorkspace && isDashboardPath(location.pathname);
  const dashboardTo = withCurrentScope(location.pathname, '/app');

  const handleWorkspaceBack = () => {
    const historyIdx = window.history.state?.idx;
    if (typeof historyIdx === 'number' && historyIdx > 0) {
      navigate(-1);
      return;
    }
    navigate(dashboardTo);
  };

  return (
    <header className="app-header zb-topbar">
      <div className="header-left">
        {onMenuClick ? (
          <button
            type="button"
            className="header-menu-toggle"
            onClick={onMenuClick}
            aria-label="Open navigation menu"
            data-navigation="true"
          >
            <FontAwesomeIcon icon={faBars} className="header-menu-toggle-icon" aria-hidden />
          </button>
        ) : null}
        {onDashboard ? (
          <Link
            to={shopsPickerTo}
            className="zb-tb-myshops"
            data-navigation="true"
            title={t('app.myShops', { defaultValue: 'My Shops' })}
          >
            <FontAwesomeIcon icon={faChevronLeft} aria-hidden />
            {t('app.myShops', { defaultValue: 'My Shops' })}
          </Link>
        ) : inWorkspace ? (
          <button
            type="button"
            className="zb-tb-back"
            data-navigation="true"
            onClick={handleWorkspaceBack}
            title={t('common.back', { defaultValue: 'Back' })}
          >
            <FontAwesomeIcon icon={faChevronLeft} aria-hidden />
            {t('common.back', { defaultValue: 'Back' })}
          </button>
        ) : null}
        <span className="zb-tb-divider" aria-hidden />
        <span className="zb-tb-title">{pageTitle}</span>
      </div>

      <div className="zb-tb-fill" />

      <div className="header-right zb-tb-right">
        <Notifications />
        <span className="zb-tb-shop-label" title={storeName}>
          {storeName || 'Shop'}
        </span>
        <ProfileMenu user={user} profileLabel={displayUserName} workspaceNav />
      </div>
    </header>
  );
};

export default Header;
